import React from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import { themeTokensOf } from './deckModel';

/**
 * Deck tab: the brief every generated deck follows, review counts and the theme tokens taken from
 * the master (the Style Kit picker arrives with the Style Kit).
 * @param {object} props - Props.
 * @param {object} props.model - Deck template.
 * @param {{fields: number, suggested: number, variables: number, fixed: number}} props.counts - Counts.
 * @param {number} props.slideCount - Slides.
 * @param {number} props.droppedCount - Slides left out.
 * @param {boolean} [props.readOnly] - No edits.
 * @param {function(object, string=): void} props.onBriefChange - Edit the brief (patch, coalesce key).
 * @param {function(): void} props.onAcceptAll - Accept every suggestion.
 * @returns {React.ReactElement}
 */
function DeckPanel({ model, counts, slideCount, droppedCount, readOnly = false, onBriefChange, onAcceptAll }) {
  const brief = model.brief && typeof model.brief === 'object' ? model.brief : {};
  const tokens = themeTokensOf(model);
  const facts = [
    `${slideCount} slide${slideCount === 1 ? '' : 's'}`,
    `${counts.fields} AI field${counts.fields === 1 ? '' : 's'}`,
    `${counts.variables} code variable${counts.variables === 1 ? '' : 's'}`,
    `${counts.fixed} fixed`,
    droppedCount ? `${droppedCount} left out` : null,
  ].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="deck-panel">
      <Box>
        <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600 }}>Deck</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{facts.join(' · ')}</Typography>
      </Box>
      {counts.suggested > 0 && !readOnly && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ flex: '1 1 160px' }}>
            {counts.suggested} suggestion{counts.suggested === 1 ? '' : 's'} waiting for review
          </Typography>
          <Button size="small" variant="outlined" startIcon={<CheckRounded />} onClick={onAcceptAll}>Accept all</Button>
        </Box>
      )}

      <Typography component="h4" sx={{ fontSize: 13, fontWeight: 600 }}>Brief</Typography>
      <TextField
        id="deck-brief-audience"
        label="Audience"
        value={brief.audience || ''}
        onChange={(event) => onBriefChange({ audience: event.target.value }, 'brief:audience')}
        fullWidth
        size="small"
        InputProps={{ readOnly }}
        inputProps={{ maxLength: 2000 }}
      />
      <TextField
        id="deck-brief-purpose"
        label="Purpose"
        value={brief.purpose || ''}
        onChange={(event) => onBriefChange({ purpose: event.target.value }, 'brief:purpose')}
        fullWidth
        multiline
        minRows={2}
        InputProps={{ readOnly }}
        inputProps={{ maxLength: 2000 }}
      />
      <TextField
        id="deck-brief-narrative"
        label="Storyline"
        value={brief.narrative || ''}
        onChange={(event) => onBriefChange({ narrative: event.target.value }, 'brief:narrative')}
        fullWidth
        multiline
        minRows={3}
        InputProps={{ readOnly }}
        inputProps={{ maxLength: 2000 }}
        helperText="How a generated deck should flow from slide to slide."
      />

      {(tokens.colors.length > 0 || tokens.fonts.length > 0) && (
        <Box>
          <Typography component="h4" sx={{ fontSize: 13, fontWeight: 600 }}>Theme from the master</Typography>
          {tokens.colors.length > 0 && (
            <Box component="ul" aria-label="Theme colours" sx={{ listStyle: 'none', m: 0, mt: 1, p: 0, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {tokens.colors.map((color) => (
                <Box component="li" key={color.name} title={`${color.name} ${color.value}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12, color: 'text.secondary' }}>
                  <Box aria-hidden="true" sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: color.value, border: '1px solid var(--q-border)' }} />
                  <span>{color.name}</span>
                </Box>
              ))}
            </Box>
          )}
          {tokens.fonts.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Fonts: {tokens.fonts.map((font) => `${font.value} (${font.name})`).join(', ')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default DeckPanel;
